import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';

import axiosInstance from '../../utility/axiosInstance';
import {handleAxiosError} from "../../helpers/handleAxiosError";

interface PrivateNetworkState {
    networks: {
        id: string;
        name: string;
        dns_domain: string | null;
        cidr?: string | null;
        subnet_id?: string | null;
        status?: string;
        isolated?: boolean;
    }[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: PrivateNetworkState = {
    networks: [],
    status: 'idle',
    error: null,
};

export const fetchPrivateNetwork = createAsyncThunk(
    'networks/fetchPrivateNetwork',
    async (_, {rejectWithValue}) => {
        console.log("🔥 fetchPrivateNetworks thunk triggered");

        try {
            const response = await axiosInstance.get('/v1/private_networks');

            if (!Array.isArray(response.data)) {
                throw new Error("Expected array of networks");
            }

            return response.data;
        } catch (error) {
            console.error("Failed to fetch private networks:", error);
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

const privateNetworkSlice = createSlice({
    name: 'privateNetwork',
    initialState,
    reducers: {
        resetPrivateNetwork: () => initialState,
        setNetworkStatus: (state, action: PayloadAction<{ id: string; status: string }>) => {
            const net = state.networks.find(n => n.id === action.payload.id);
            if (net) {
                net.status = action.payload.status;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPrivateNetwork.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchPrivateNetwork.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.status = 'succeeded';
                state.networks = action.payload.map(net => ({
                    id: net.id,
                    name: net.name,
                    dns_domain: net.dns_domain || null,
                    cidr: net.cidr || null,
                    subnet_id: net.subnet_id || null,
                    status: 'active',
                    isolated: net.isolated === true,
                }));
            })
            .addCase(fetchPrivateNetwork.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.networks = [];
            });
    },
});

export const {resetPrivateNetwork, setNetworkStatus} = privateNetworkSlice.actions;
export default privateNetworkSlice.reducer;